import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { MobileFrame } from "@/components/MobileFrame";
import { Card, Chip, Hairline, Icon, Section } from "@/components/primitives";
import { EvidenceChecklist } from "@/components/EvidenceChecklist";
import { FLAG_LABEL, type EvidenceFlag } from "@/lib/evidenceChecklist";

// Reusable, always-available reference for collectors. Linked from the profile
// page so users can review what makes a great scan and what evidence each
// flagged category needs — without going through the live verification flow.

const FLAGS: EvidenceFlag[] = ["fur", "leather", "luxury", "exotic"];

const GENERAL_TIPS: { title: string; body: string }[] = [
  {
    title: "Shoot in daylight, on a plain surface",
    body: "Indirect window light beats overhead bulbs. A neutral floor or wall keeps fig's eye on the garment, not the room.",
  },
  {
    title: "Get within 6 inches of the label",
    body: "Care tags, brand stamps, and union labels carry the most signal. Fill the frame with them — it's the single biggest thing you can do for a confident read.",
  },
  {
    title: "Add a stitching close-up",
    body: "Single vs. chain stitch, thread weight, and seam construction date a garment more reliably than the label alone.",
  },
  {
    title: "Don't skip the hardware",
    body: "Zippers (Talon, YKK, Conmar), buttons, rivets, and snaps are dating gold. One macro shot is enough.",
  },
  {
    title: "Up to 5 photos per scan",
    body: "fig analyses every angle and merges the verdict. More angles = sharper read, especially on flagged categories.",
  },
  {
    title: "One scan, three roads",
    body: "Once verified, you can file the piece privately to your archive, share it to your closet, or draft a listing for your linked shop. You don't have to decide up front.",
  },
];

export default function BestPractices() {
  const nav = useNavigate();
  const [openFlag, setOpenFlag] = useState<EvidenceFlag | null>("fur");

  return (
    <MobileFrame>
      <div className="paper-bg" style={{ minHeight: "100vh", paddingBottom: 60 }}>
        {/* Top bar */}
        <div style={{
          padding: "12px 20px 0",
          display: "flex", justifyContent: "space-between", alignItems: "center",
        }}>
          <button
            onClick={() => nav(-1)}
            style={{
              background: "none", border: "none", padding: 6, cursor: "pointer",
              color: "hsl(var(--ink))",
            }}
            aria-label="Back"
          >
            {Icon.chev(18, "l")}
          </button>
          <span className="caps">Best practices</span>
          <div style={{ width: 30 }} />
        </div>

        {/* Hero */}
        <Section pad={14} eyebrow="Field guide">
          <div style={{
            background: "hsl(var(--ink))",
            color: "hsl(var(--cream))",
            padding: "20px 22px 18px",
          }}>
            <div style={{
              fontFamily: "'Fraunces', 'Playfair Display', serif", fontWeight: 900,
              fontSize: 34, lineHeight: 1, letterSpacing: "-0.02em",
            }}>
              How to scan well.
            </div>
            <div style={{
              marginTop: 8,
              fontFamily: "'Fraunces', 'Playfair Display', serif",
              fontSize: 14, lineHeight: 1.4, color: "hsl(var(--cream))",
            }}>
              fig isn't a marketplace — it's the archive that sits behind your
              collection. The difference between a confident verdict and a
              shaky one is usually one extra photo. Here's what fig wants to see.
            </div>
          </div>
        </Section>

        {/* General tips */}
        <Section pad={20} eyebrow="Every scan" title="Five quick rules">
          <Card style={{ padding: 0 }}>
            {GENERAL_TIPS.map((tip, i) => (
              <div key={tip.title} style={{
                padding: "12px 14px",
                borderTop: i > 0 ? "1px dashed hsl(var(--sand-deep))" : "none",
                display: "flex", gap: 12,
              }}>
                <div style={{
                  width: 22, height: 22, flexShrink: 0,
                  background: "hsl(var(--rust))", color: "hsl(var(--rust-fg))",
                  display: "grid", placeItems: "center",
                  fontFamily: "'IBM Plex Mono', monospace", fontSize: 10, fontWeight: 700,
                }}>
                  {String(i + 1).padStart(2, "0")}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{
                    fontFamily: "'Fraunces', 'Playfair Display', serif", fontWeight: 700,
                    fontSize: 15, color: "hsl(var(--ink))", lineHeight: 1.2,
                  }}>
                    {tip.title}
                  </div>
                  <div style={{
                    marginTop: 3,
                    fontFamily: "'Fraunces', 'Playfair Display', serif",
                    fontSize: 13, color: "hsl(var(--ink))", lineHeight: 1.4,
                  }}>
                    {tip.body}
                  </div>
                </div>
              </div>
            ))}
          </Card>
        </Section>

        {/* How hints work — the conflict rule */}
        <Section pad={20} eyebrow="The hint box" title="How notes are weighed">
          <Card style={{ padding: "14px" }}>
            <div style={{
              fontFamily: "'Fraunces', 'Playfair Display', serif",
              fontSize: 13, color: "hsl(var(--ink))", lineHeight: 1.45,
            }}>
              Anything you type in the hint box before scanning is treated as
              <strong> unverified context</strong>. fig weights it when the photo
              backs it up — and quietly overrides it when the photo doesn't.
            </div>

            <Hairline dashed style={{ margin: "12px 0" }} />

            <div style={{
              fontFamily: "'IBM Plex Mono', monospace", fontSize: 10,
              letterSpacing: "0.14em", textTransform: "uppercase",
              color: "hsl(var(--rust))", fontWeight: 700, marginBottom: 8,
            }}>
              Worked example
            </div>

            <div style={{
              padding: "10px 12px",
              border: "1px solid hsl(var(--sand-deep))",
              background: "hsl(var(--paper-3))",
              fontFamily: "'IBM Plex Mono', monospace", fontSize: 11,
              color: "hsl(var(--ink))", lineHeight: 1.5,
            }}>
              <div><strong>You type:</strong> "vintage 70s wool, made in USA"</div>
              <div style={{ marginTop: 4 }}><strong>Tag reads:</strong> SHEIN · made in China · 2024</div>
            </div>

            <div style={{
              marginTop: 12,
              fontFamily: "'Fraunces', 'Playfair Display', serif",
              fontSize: 13, color: "hsl(var(--ink))", lineHeight: 1.5,
            }}>
              fig will side with the tag every time. Two things then happen:
            </div>

            <ul style={{
              margin: "8px 0 0 18px", padding: 0,
              fontFamily: "'Fraunces', 'Playfair Display', serif",
              fontSize: 13, color: "hsl(var(--ink))", lineHeight: 1.5,
            }}>
              <li style={{ marginBottom: 6 }}>
                <strong>Shein is on the auto-fail list</strong> — verdict flips
                to FAIL even if the rest of the scan looked vintage-styled.
              </li>
              <li>
                <strong>If the conflict isn't a hard-fail brand</strong> but the
                main label isn't clearly photographed, fig blocks the PASS with
                <em> "Label proof needed"</em> and asks for a close-up of the
                brand tag before changing its mind.
              </li>
            </ul>

            <Hairline dashed style={{ margin: "12px 0" }} />

            <div style={{
              fontFamily: "'IBM Plex Mono', monospace", fontSize: 10,
              letterSpacing: "0.14em", textTransform: "uppercase",
              color: "hsl(var(--olive))", fontWeight: 700, marginBottom: 6,
            }}>
              Hints that help
            </div>
            <div style={{
              fontFamily: "'Fraunces', 'Playfair Display', serif",
              fontSize: 13, color: "hsl(var(--ink-2))", lineHeight: 1.45,
            }}>
              "Real fur — feels like skin underneath," "Smells musty / definitely
              old," "Tag is faded but I think it says Pendleton" — concrete
              observations fig can corroborate against the photo. Avoid pitching
              an era or brand the tag doesn't support; it won't help and may
              trigger the label-proof block.
            </div>
          </Card>
        </Section>

        {/* Per-category checklists */}
        <Section pad={20} eyebrow="Sensitive categories" title="Evidence required">
          <div style={{
            fontFamily: "'Fraunces', 'Playfair Display', serif",
            fontSize: 13, color: "hsl(var(--ink-2))", lineHeight: 1.4, marginBottom: 12,
          }}>
            Fur, leather, luxury, and exotic-skin pieces get a verification block on
            the result page. Capture these shots up front to skip the back-and-forth.
          </div>

          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 12 }}>
            {FLAGS.map((f) => {
              const active = openFlag === f;
              return (
                <button
                  key={f}
                  onClick={() => setOpenFlag(active ? null : f)}
                  style={{
                    padding: "6px 12px",
                    border: "1px solid hsl(var(--ink))",
                    background: active ? "hsl(var(--ink))" : "hsl(var(--cream))",
                    color: active ? "hsl(var(--cream))" : "hsl(var(--ink))",
                    cursor: "pointer",
                    fontFamily: "'IBM Plex Mono', monospace", fontSize: 10,
                    letterSpacing: "0.12em", textTransform: "uppercase",
                  }}
                >
                  {FLAG_LABEL[f]}
                </button>
              );
            })}
          </div>

          {openFlag && (
            <Card style={{ padding: "14px" }}>
              <EvidenceChecklist flag={openFlag} reference />
            </Card>
          )}
        </Section>

        {/* CTA */}
        <Section pad={22}>
          <button
            onClick={() => nav("/scan")}
            className="btn btn-primary"
            style={{ width: "100%", padding: "14px" }}
          >
            Scan something →
          </button>
        </Section>
      </div>
    </MobileFrame>
  );
}

function TierRow({ color, label, body }: { color: string; label: string; body: string }) {
  return (
    <div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
      <div style={{
        width: 12, height: 12, flexShrink: 0, marginTop: 4,
        background: color, border: "1px solid hsl(var(--ink))",
      }} />
      <div style={{ flex: 1 }}>
        <div style={{
          fontFamily: "'IBM Plex Mono', monospace", fontSize: 10,
          letterSpacing: "0.14em", textTransform: "uppercase",
          color, fontWeight: 700,
        }}>
          {label}
        </div>
        <div style={{
          marginTop: 3,
          fontFamily: "'Fraunces', 'Playfair Display', serif",
          fontSize: 13, color: "hsl(var(--ink))", lineHeight: 1.4,
        }}>
          {body}
        </div>
      </div>
    </div>
  );
}